import React, { Component, Fragment } from "react";
import { injectIntl } from "react-intl";
import { addDays } from "./services/clubmed";

import "./DatePicker.css";

const formatDate = (date, separator = "-") =>
  `${date.getFullYear()}${separator}${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${separator}${date
    .getDate()
    .toString()
    .padStart(2, "0")}`;

const buildDateRange = (from, to) => {
  const range = [];
  const f = new Date(from);
  while (f <= to) {
    const current = new Date(formatDate(f));
    range.push(current);
    f.setDate(f.getDate() + 1);
  }
  return range;
};

const buildWeeks = month => ({
  year: month.year,
  month: month.month,
  weeks: month.dates.reduce((acc, date) => {
    const count = acc.length;
    const day = date.getDay();
    return day === 1
      ? [...acc, [date]]
      : count === 0
        ? [...acc, [date]]
        : acc.map(
            (week, index) => (index === count - 1 ? [...week, date] : week)
          );
  }, [])
});

const buildMonths = range => {
  const months = range.reduce((acc, date) => {
    const month = date.getMonth();
    const year = date.getFullYear();
    const key = `${year}-${month}`;
    return acc[key] !== undefined
      ? { ...acc, [key]: { ...acc[key], dates: [...acc[key].dates, date] } }
      : { ...acc, [key]: { ...acc[key], month, year, dates: [date] } };
  }, {});

  return Object.keys(months).reduce((acc, key) => {
    return { ...acc, [key]: buildWeeks(months[key]) };
  }, {});
};

const isSelected = (day, start, stop) => {
  if (!start) return false;
  const ref = formatDate(day.date);
  const kind = day.kind;
  return (
    (!stop && ref === formatDate(start) && kind) ||
    (stop && ref >= formatDate(start) && ref <= formatDate(stop))
  );
};

const fulfillWeek = week => {
  if (week.length === 7) return week;
  const first = week[0];
  const firstDay = (first.getDay() + 6) % 7;

  for (let i = 0; i < firstDay; i++) {
    week.unshift(null);
  }

  for (let i = week.length; i < 7; i++) {
    week.push(null);
  }

  return week;
};

const findScrollable = el => {
  return el.className.indexOf("datepicker__month") !== -1
    ? el
    : findScrollable(el.parentNode);
};

const initMonths = (from, to) => {
  return buildMonths(buildDateRange(from, to));
};

class DatePicker extends Component {
  state = {
    selection: this.props.selected || {},
    months: initMonths(this.props.from, this.props.to)
  };

  componentDidMount = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(this.scroll);
    });
  };

  shouldComponentUpdate = (nextProps, nextState) => {
    const { from, to, selected, rangePicker } = this.props;
    const { selection, months } = this.state;
    return !(
      selection === nextState.selection &&
      months === nextState.months &&
      from === nextProps.from &&
      to === nextProps.to &&
      selected === nextProps.selected &&
      rangePicker === nextProps.rangePicker
    );
  };

  scroll = () => {
    const el = this.refs["datepicker__start"];
    if (!el) return;
    this.refs.growable.scrollTop = findScrollable(el).offsetTop - 24;
  };

  setSelection = (start, stop) => {
    this.setState({
      selection: { start, stop }
    });
    this.props.selectionChanged({ start, stop });
  };

  rangeClicked = day => {
    const { start, stop } = this.state.selection;
    if (start && stop) this.setSelection(day, undefined);
    if (!start) this.setSelection(day, undefined);
    if (start && !stop) {
      if (start > day) {
        const stop = start;
        this.setSelection(day, stop);
      } else {
        if (start === day) {
          this.setSelection(day, undefined);
        } else {
          this.setSelection(start, day);
        }
      }
    }
  };

  dateClicked = day => {
    this.setSelection(day, undefined);
  };

  clicked = day => () => {
    this.props.rangePicker ? this.rangeClicked(day) : this.dateClicked(day);
  };

  startClass = day => {
    const { start } = this.state.selection;
    return start instanceof Date
      ? formatDate(day) === formatDate(start)
        ? "datepicker__start"
        : undefined
      : undefined;
  };

  stopClass = day => {
    const { start, stop } = this.state.selection;
    return stop instanceof Date
      ? formatDate(day) === formatDate(stop)
        ? "datepicker__stop"
        : undefined
      : start instanceof Date
        ? formatDate(day) === formatDate(start)
          ? "datepicker__stop"
          : undefined
        : undefined;
  };

  selectedClass = selected => (selected ? "datepicker__selected" : undefined);

  selectCurrentWeek = () => {
    const stop = new Date();
    const start = new Date();
    const currentDay = start.getDay();
    const dayToAdd = currentDay === 6 ? 1 : 0;
    stop.setDate(
      start.getDate() + dayToAdd + ((6 + (7 - (start.getDay() + dayToAdd))) % 7)
    );

    this.setState({ selection: { start, stop } }, () => {
      this.props.selectionChanged({ start, stop });
      this.scroll();
    });
  };

  selectNextWeek = () => {
    const start = new Date();
    const currentDay = start.getDay();
    const dayToAdd = currentDay === 6 ? 1 : 0;
    start.setDate(
      start.getDate() + dayToAdd + ((6 + (7 - (start.getDay() + dayToAdd))) % 7)
    );

    const stop = addDays(start, 7);

    this.setState({ selection: { start, stop } }, () => {
      this.props.selectionChanged({ start, stop });
      this.scroll();
    });
  };

  renderOnDay = (day, selected) => {
    const selectedClass = this.selectedClass(selected);
    const startClass = this.startClass(day);
    const stopClass = this.stopClass(day);
    const classes = [
      "datepicker__calendar-li",
      selectedClass,
      startClass,
      stopClass
    ].join(" ");
    return (
      <Fragment key={day.getTime()}>
        <li
          ref={this.startClass(day)}
          className={classes}
          onClick={this.clicked(day)}
        >
          {day.getDate()}
        </li>
      </Fragment>
    );
  };

  renderOffDay = (key, day, selected) => {
    const selectedClass = this.selectedClass(selected);
    const classes = ["datepicker__calendar-li", "off", selectedClass].join(" ");
    return (
      <Fragment key={key}>
        <li className={classes} />
      </Fragment>
    );
  };

  renderDay = (key, day, selected) =>
    day
      ? this.renderOnDay(day, selected)
      : this.renderOffDay(key, day, selected);

  renderWeekDay = () => {
    const { intl } = this.props;
    const weekDay = [];
    const date = new Date();
    while (date.getDay() !== 1) {
      date.setDate(date.getDate() + 1);
    }
    for (let i = 0; i < 7; i++) {
      weekDay.push(intl.formatDate(formatDate(date), { weekday: "short" }));
      date.setDate(date.getDate() + 1);
    }
    return weekDay.map((item, idx) => (
      <Fragment key={idx}>
        <span className="datepicker__header-month">{item}</span>
      </Fragment>
    ));
  };

  renderCalendarTitle = month => {
    const { intl } = this.props;
    const date = new Date(2018, month);
    return (
      <Fragment>
        <span className="datepicker__date">
          {intl.formatDate(formatDate(date), { month: "long" })}
        </span>
      </Fragment>
    );
  };

  renderCalendar = key => {
    const months = this.state.months;
    const { start, stop } = this.state.selection;
    let refDay = { date: this.props.from, kind: false };
    return (
      <Fragment>
        <div className="datepicker__overlay-top" />
        <div className="datepicker__overlay-bottom" />
        <div ref="growable" className="datepicker__growable">
          <div className="datepicker__growable-inner">
            {Object.keys(months).map(key => (
              <div key={key} className="datepicker__month">
                {this.renderCalendarTitle(months[key].month)}
                <div className="datepicker__calendar-month">
                  {months[key].weeks.map((week, index) => (
                    <ul
                      className="datepicker__calendar-month-ul"
                      key={`${key}-${index}`}
                    >
                      {fulfillWeek(week).map((day, indexDay) => {
                        refDay = day
                          ? { date: day, kind: true }
                          : { date: refDay.date, kind: false };
                        const selected = isSelected(refDay, start, stop);
                        return this.renderDay(
                          `${refDay.date.getTime()}-${indexDay}`,
                          day,
                          selected
                        );
                      })}
                    </ul>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Fragment>
    );
  };

  renderDisableLayer() {
    const { disable } = this.props;
    if (disable) {
      return <div className="datepicker__overlay" />;
    }
  }

  renderShortcuts() {
    return (
      <div className="datepicker__shortcut">
        <button onClick={this.selectCurrentWeek}>Semaine courante</button>
        <button onClick={this.selectNextWeek}>Semaine suivante</button>
      </div>
    );
  }

  render() {
    return (
      <div className="datepicker__calendar">
        <div className="datepicker__calendar-header">
          {this.renderWeekDay()}
        </div>
        {this.renderCalendar()}
        {this.renderDisableLayer()}
        {this.renderShortcuts()}
      </div>
    );
  }
}

export default injectIntl(DatePicker);



// WEBPACK FOOTER //
// ./src/DatePicker.js